import { Anchor } from '@mantine/core';
import { RefObject, useEffect, useRef, useState } from 'react';
import classes from '../../../app.module.css';
import { ReportHistoryCarousel } from './reportHistoryCarousel';
import { ParameterHistory } from './parameterHistory';
import { ComparisonParameterHistory } from './comparisonParameterHistory';
import { CategorizedParameterHistory } from './categorizedParameterHistory';
import {
  categoryParameterHistoryData,
  comparisonParameterHistoryData,
  GraphTypes,
  parameterHistoryData,
} from '../stores';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Parameters } from '@web/common';

export interface historyData {
  ref?: RefObject<HTMLDivElement | null>;
  type: GraphTypes;
  parameter: Parameters;
  parameter2?: Parameters;
  unit: string;
  unit2?: string;
  data:
    | parameterHistoryData[]
    | comparisonParameterHistoryData[]
    | categoryParameterHistoryData[];
  thresholds?: number[];
  infoText: string;
}

export const HistoryCategory = ({
  parameters,
  maxWidth,
}: {
  parameters: historyData[];
  maxWidth: number;
}) => {
  const { t } = useTranslation('reports');
  const [selectedParameter, setSelectedParameter] = useState(
    parameters[0].parameter,
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const carousel = useRef<HTMLDivElement>(null);
  const [hoveringCharts, setHoveringCharts] = useState(true);

  useEffect(() => {
    if (searchParams.get('parameter')) {
      const parameter = searchParams.get('parameter') as Parameters;
      const index = parameters.findIndex(
        (entry) =>
          entry.parameter === parameter || entry.parameter2 === parameter,
      );
      setSelectedParameter(parameter);
      setSearchParams();
      if (carousel.current) {
        carousel.current.scrollTo({
          left: index * Math.min(window.screen.width, maxWidth),
          top: 0,
          behavior: 'smooth',
        });
      }
    }
  }, [searchParams, t, parameters, setSearchParams, maxWidth]);

  function handleClick(parameter: Parameters, target: number) {
    setSelectedParameter(parameter);
    if (carousel.current) {
      carousel.current.scrollTo({
        left: target * Math.min(window.screen.width, maxWidth),
        top: 0,
        behavior: 'smooth',
      });
    }
  }

  const anchorArray = parameters.map((entry, index) => {
    return (
      <Anchor
        key={entry.parameter}
        style={{ whiteSpace: 'nowrap' }}
        onClick={() => handleClick(entry.parameter, index)}
        className={
          selectedParameter === entry.parameter
            ? classes.carouselButtonActive
            : classes.carouselButton
        }
      >
        {t(`reports:${entry.parameter}`)}
      </Anchor>
    );
  });

  useEffect(() => {
    function handleScroll(event: Event) {
      if (carousel.current && hoveringCharts) {
        const scrollPos = carousel.current.scrollLeft;
        const interval = Math.min(window.screen.width, maxWidth) - 16;
        setSelectedParameter(
          parameters[Math.floor((scrollPos + interval / 2) / interval)]
            .parameter,
        );
      }
    }
    const element = carousel.current;
    if (element) {
      element.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (element) {
        element.removeEventListener('scroll', handleScroll);
      }
    };
  }, [maxWidth, parameters, hoveringCharts]);

  const chartArray = parameters.map((entry) => {
    const chart = () => {
      switch (entry.type) {
        case GraphTypes.VALUE:
          return (
            <ParameterHistory
              key={entry.parameter}
              parameter={entry.parameter}
              parameter2={entry.parameter2}
              infoText={entry.infoText}
              data={entry.data as parameterHistoryData[]}
              unit={entry.unit}
              unit2={entry.unit2}
              maxWidth={maxWidth}
            />
          );
        case GraphTypes.COMPARISON:
          return (
            <ComparisonParameterHistory
              key={entry.parameter}
              parameter={entry.parameter}
              infoText={entry.infoText}
              data={entry.data as comparisonParameterHistoryData[]}
              unit={entry.unit}
              maxWidth={maxWidth}
            />
          );
        case GraphTypes.CATEGORY:
          return (
            <CategorizedParameterHistory
              key={entry.parameter}
              parameter={entry.parameter}
              infoText={entry.infoText}
              data={entry.data as categoryParameterHistoryData[]}
              thresholds={entry.thresholds || []}
              maxWidth={maxWidth}
            />
          );
      }
    };

    return chart();
  });

  return (
    <>
      <ReportHistoryCarousel onMouseDown={() => setHoveringCharts(false)}>
        {anchorArray}
      </ReportHistoryCarousel>
      <ReportHistoryCarousel
        ref={carousel}
        onMouseEnter={() => setHoveringCharts(true)}
        onMouseDown={() => setHoveringCharts(true)}
        onTouchStart={() => setHoveringCharts(true)}
      >
        {chartArray}
      </ReportHistoryCarousel>
    </>
  );
};
